import { useMutation } from "@apollo/client";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type {
	ProcessXeroAuthCallbackMutation,
	ProcessXeroAuthCallbackMutationVariables,
} from "~/api/process-xero-auth-callback.generated";
import processXeroAuthCallbackMutation from "~/api/process-xero-auth-callback.graphql";

const XERO_CALLBACK_URL_PARAMS_KEYS = ["code", "scope", "session_state"];

function XeroAuthCallback() {
	const [isXeroConnected, setIsXeroConnected] = useState(false);
	const [error, setError] = useState<string>();

	const [processXeroAuthCallback, { loading: saving }] = useMutation<
		ProcessXeroAuthCallbackMutation,
		ProcessXeroAuthCallbackMutationVariables
	>(processXeroAuthCallbackMutation, {
		onCompleted(data) {
			setIsXeroConnected(data.processXeroAuthCallback.isTokenSetValid ?? false);
		},
		onError(e) {
			setError(e.toString());
		},
	});

	const location = useLocation();

	const hasXeroKeys = useMemo(() => {
		const params = new URLSearchParams(location.search);
		const keys = Array.from(params.keys());
		return XERO_CALLBACK_URL_PARAMS_KEYS.every((key) => keys.includes(key));
	}, [location.search]);

	useEffect(() => {
		if (hasXeroKeys) {
			void (async () => {
				await processXeroAuthCallback({
					variables: {
						callbackUrl: `${location.pathname}${location.search}`,
					},
				});
			})();
		} else {
			setError("Invalid Xero callback URL");
		}
	}, [hasXeroKeys, location, processXeroAuthCallback]);

	return (
		<div style={{ margin: "1em" }}>
			<p>
				Xero Connection:{" "}
				{isXeroConnected ? (
					<span style={{ color: "green" }}>Connected</span>
				) : (
					<span style={{ color: "red" }}>Not connected</span>
				)}
			</p>
			{saving ? (
				<div>Processing Xero callback. Please wait...</div>
			) : (
				<div>
					<p>
						Xero callback processed.{" "}
						{error && <span style={{ color: "red" }}>{error}</span>}
					</p>
					<Link to="/#/inventorySettings">Go to Inventory Settings</Link>
				</div>
			)}
		</div>
	);
}

export default XeroAuthCallback;
